/**
 * Payment Failure Page
 * 
 * Displayed when payment fails.
 * Provides option to retry or go back to pricing.
 */
import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { 
  XCircle, 
  RefreshCw, 
  ArrowLeft,
  HelpCircle,
  Phone,
  Mail
} from 'lucide-react';

const PaymentFailure = () => {
  const location = useLocation();
  const state = location.state;
  
  // Redirect to pricing if no state (direct access)
  if (!state) {
    return <Navigate to="/pricing" replace />;
  }
  
  const { error, planName } = state;

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Failure Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Failure Icon */}
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="text-red-600" size={48} />
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Failed
          </h1>
          
          {/* Error Message */}
          <p className="text-gray-600 mb-4">
            Unfortunately, your payment could not be processed.
          </p>
          
          {/* Error Details */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-red-700">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}
          
          {/* Plan Info */}
          {planName && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                Plan: <span className="font-semibold text-gray-900">{planName}</span>
              </p>
            </div>
          )}
          
          {/* Common Reasons */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <HelpCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">
                  Common Reasons for Payment Failure
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                  <li>Insufficient funds in your account</li>
                  <li>Card expired or blocked for online transactions</li>
                  <li>Incorrect card details or OTP</li>
                  <li>Bank server issues</li>
                  <li>Transaction timeout</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="space-y-3">
            <Link
              to="/pricing"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
            >
              <RefreshCw size={20} />
              Try Again
            </Link>
            
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-full font-semibold hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </div>
        </div>
        
        {/* Support Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">
            Need Help?
          </h3>
          <div className="space-y-3">
            <a 
              href="tel:+918591115054"
              className="flex items-center gap-3 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <Phone size={18} />
              <span>+91 85911 15054</span>
            </a>
            <a 
              href="mailto:support@climbcapital.in"
              className="flex items-center gap-3 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <Mail size={18} />
              <span>support@climbcapital.in</span>
            </a>
          </div>
          <p className="text-xs text-gray-500 mt-4 text-center">
            If amount was deducted, it will be refunded within 5-7 business days.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailure;
